import { state, style, trigger } from '@angular/animations';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { PayloadHelper } from '@app/api/core/utils/helper/payload-helper';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { ReminderComponent } from '@app/shared/components/reminder/reminder.component';
import { ThrottleEventDirective } from '@app/shared/directives/throttle-event.directive';
import { FormatAdressPipe } from '@app/shared/pipes/formatadress.pipe';
import { FormatDatePipe } from '@app/shared/pipes/formatdate.pipe';
import { DialogStatus } from '@app/shared/services/ui/dialog/dialog.types';
import { DisplayOption } from '@app/shared/services/ui/message/message';
import { ExpandableRow } from '@app/views/vertriebscockpit/offerteantraege/offerteantraege.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Polizzentyp, PolizzeResult } from '@o30/entities';
import { EMPTY } from 'rxjs';
import { first, switchMap, take } from 'rxjs/operators';
import { BasicPolizzeListComponent } from '../basic-polizze-list/basic-polizze-list.component';

@UntilDestroy()
@Component({
  selector:    'app-vorlaufige-deckungen',
  templateUrl: './vorlaufige-deckungen.component.html',
  styleUrls:   ['./vorlaufige-deckungen.component.scss'],
  animations:  [
    trigger('detailExpand', [
      state('collapsed', style({
        display: 'none'
      })),
      state('expanded', style({
        height:     '*',
        visibility: 'visible'
      }))
    ])
  ],
  standalone:  true,
  imports:     [
    NgIf,
    MatTable,
    MatSort,
    NgFor,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    MatTooltip,
    ThrottleEventDirective,
    ReminderComponent,
    NgClass,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    MatProgressSpinner,
    NoDataComponent,
    AsyncPipe,
    FormatDatePipe,
    FormatAdressPipe
  ]
})

export class VorlaufigeDeckungenComponent extends BasicPolizzeListComponent implements OnInit,
                                                                                       OnDestroy {

  public saveAsBerEmpf(row: PolizzeResult): void {
    this.llapi.patchValue('layout.spinner', true);
    this.dataService.savePolizzeOffert2(row.id)
      .pipe(
        first(),
        untilDestroyed(this))
      .subscribe(payload => {
        this.llapi.patchValue('layout.spinner', false);
        if (payload && !PayloadHelper.hasError(payload)) {
          this.messageService.handlePayload(payload, {displayOption: DisplayOption.SNACKBAR});
        } else {
          if (payload?.data) {
            this.messageService.handlePayload(payload, {displayOption: DisplayOption.MONITOR});
          }
          this.routerService.routeOffertStartPage(row.vke.id, row.id);
        }
      });
  }

  requestExtend(row: PolizzeResult): void {
    this.dialogService.displayFreieBemerkungDialog(
        undefined, undefined,
        'Beantragung zur Verlängerung der vorläufigen Deckung')
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this),
        switchMap(_dialogResult => _dialogResult.statusId === DialogStatus.SAVE_SUCCESS
          ? this.dataService.extendPolizzeDeckung(row.id, _dialogResult.data)
          : EMPTY)
      )
      .subscribe(_payload => {
        if (this.messageService.handlePayloadReturnSuccess(_payload)) {
          this.messageService.showSnackbarSaveSuccess();
        }
      });
  }

  isOffen(row: PolizzeResult): boolean {
    return row.typ === Polizzentyp.DECKUNG_OFFEN;
  }

  storno(row: PolizzeResult): void {
    row.typ === Polizzentyp.DECKUNG_OFFEN
      ? this.stornoDeckungOffen(row)
      : this.stornoDeckung(row);
  }

  stornoDeckung(row: PolizzeResult): void {
    this.dialogService.displayFreieBemerkungDialog(
        undefined, undefined,
        'Beantragung zur Stornierung der vorläufigen Deckung')
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this),
        switchMap(_dialogResult => _dialogResult.statusId === DialogStatus.SAVE_SUCCESS
          ? this.dataService.stornoPolizzeDeckung(row.id, _dialogResult.data)
          : EMPTY
        )
      )
      .subscribe(_payload => {
        if (this.messageService.handlePayloadReturnSuccess(_payload)) {
          this.messageService.showSnackbarSaveSuccess();
        }
      });
  }

  stornoDeckungOffen(row: PolizzeResult): void {
    this.messageService.showSimpleOkCancelDialog(
        'Soll die offene Änderung der vorläufigen Deckung storniert werden?')
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this),
        switchMap(_dialogResult =>
          _dialogResult && _dialogResult.status === 'ok'
            ? this.dataService.stornoPolizzeDeckungOffen(row.id) : EMPTY)
      )
      .subscribe(_payload => {
        if (this.messageService.handlePayloadReturnSuccess(_payload)) {
          this.removeFromList(row.id);
          this.messageService.showSnackbarSaveSuccess();
        }
      });
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--override superclass--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  // @Override
  getAdditionalColumns() {
    return [
      {
        columnDef: 'deckvariante',
        header:    'Deckung Variante',
        cell:      (row: ExpandableRow<PolizzeResult>) => `${row.data.langbezeichnung}`
      }
    ];
  }

  // @Override
  get displayedColumns(): string[] {
    const columns = this.tableColumns.map(c => c.columnDef);
    columns.unshift('expand');
    if (this.bearbeitenErlaubt) {
      columns.push('aktionen');
      columns.push('nextoffert');
    }
    return columns;
  }

  getAnzRowsPerEntry(): number {
    return 2;
  }
}
